import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Loader2, ScanSearch, FileText } from 'lucide-react';

const STAGES = [
  { id: 'new', label: 'New', dot: 'bg-slate-400', accent: 'from-slate-400/70 via-slate-400/25 to-transparent', chip: 'bg-slate-400/10 text-slate-400' },
  { id: 'contacted', label: 'Contacted', dot: 'bg-sky-400', accent: 'from-sky-400 via-sky-400/30 to-transparent', chip: 'bg-sky-400/10 text-sky-400' },
  { id: 'proposal_sent', label: 'Proposal Sent', dot: 'bg-violet-400', accent: 'from-violet-400 via-violet-400/30 to-transparent', chip: 'bg-violet-400/10 text-violet-400' },
  { id: 'meeting_booked', label: 'Meeting Booked', dot: 'bg-amber-400', accent: 'from-amber-400 via-amber-400/30 to-transparent', chip: 'bg-amber-400/10 text-amber-400' },
  { id: 'won', label: 'Won', dot: 'bg-emerald-400', accent: 'from-emerald-400 via-emerald-400/30 to-transparent', chip: 'bg-emerald-400/10 text-emerald-400' },
  { id: 'lost', label: 'Lost', dot: 'bg-red-400', accent: 'from-red-400 via-red-400/30 to-transparent', chip: 'bg-red-400/10 text-red-400' },
];

const AVATAR_GRADIENTS = [
  'from-teal-500/80 to-emerald-600/80', 'from-violet-500/80 to-fuchsia-600/80',
  'from-sky-500/80 to-cyan-600/80', 'from-amber-500/80 to-orange-600/80',
  'from-rose-500/80 to-pink-600/80',
];
const avatarFor = (name) => AVATAR_GRADIENTS[(name || 'x').charCodeAt(0) % AVATAR_GRADIENTS.length];

export const PipelineBoard = ({ theme, api, workspaceId, onOpenLead }) => {
  const { isDarkMode: dark, textPrimary, textSecondary } = theme;
  const [data, setData] = useState(null);
  const [overStage, setOverStage] = useState(null);
  const [expanded, setExpanded] = useState([]);

  const load = useCallback(async () => {
    try {
      const q = expanded.length ? `?expand=${expanded.join(',')}` : '';
      const r = await api.get(`/api/workspaces/${workspaceId}/leads/pipeline${q}`);
      setData(r.data);
    } catch { toast.error('Failed to load pipeline'); }
  }, [api, workspaceId, expanded]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const fn = () => load();
    window.addEventListener('voxinity:lead-updated', fn);
    return () => window.removeEventListener('voxinity:lead-updated', fn);
  }, [load]);

  const moveCard = async (leadId, from, to) => {
    if (!leadId || from === to) return;
    setData(d => {
      const card = d.stages[from]?.find(c => c.lead_id === leadId);
      if (!card) return d;
      return {
        ...d,
        stages: {
          ...d.stages,
          [from]: d.stages[from].filter(c => c.lead_id !== leadId),
          [to]: [card, ...d.stages[to]],
        },
        counts: { ...d.counts, [from]: d.counts[from] - 1, [to]: d.counts[to] + 1 },
      };
    });
    try {
      await api.put(`/api/workspaces/${workspaceId}/leads/${leadId}`, { status: to });
      toast.success(`Moved to ${STAGES.find(s => s.id === to)?.label}`);
    } catch {
      toast.error('Failed to move — reloading');
      load();
    }
  };

  if (!data) {
    return <div className="py-20 flex justify-center"><Loader2 size={22} className="animate-spin text-teal-400" /></div>;
  }

  return (
    <div className="grid grid-cols-6 gap-3 items-start max-lg:flex max-lg:overflow-x-auto max-lg:pb-4" data-testid="pipeline-board">
      {STAGES.map(s => {
        const cards = data.stages[s.id] || [];
        const total = data.counts[s.id] || 0;
        const isOver = overStage === s.id;
        return (
          <div key={s.id}
            onDragOver={e => { e.preventDefault(); setOverStage(s.id); }}
            onDragLeave={() => setOverStage(o => (o === s.id ? null : o))}
            onDrop={e => {
              e.preventDefault(); setOverStage(null);
              moveCard(e.dataTransfer.getData('leadId'), e.dataTransfer.getData('from'), s.id);
            }}
            className={`min-w-0 max-lg:w-[248px] max-lg:shrink-0 rounded-2xl border overflow-hidden backdrop-blur-xl transition-all duration-300 ${
              isOver
                ? 'border-teal-400/60 ' + (dark ? 'bg-teal-400/[0.06] shadow-[0_0_28px_-10px_rgba(45,212,191,0.5)]' : 'bg-teal-50 shadow-[0_0_28px_-12px_rgba(20,184,166,0.45)]')
                : dark
                  ? 'border-white/10 bg-gradient-to-b from-white/[0.045] to-white/[0.01]'
                  : 'border-slate-200 bg-white shadow-[0_10px_30px_-20px_rgba(2,6,23,0.25)]'}`}
            data-testid={`pipeline-col-${s.id}`}>
            {/* stage accent line */}
            <div className={`h-[3px] bg-gradient-to-r ${s.accent}`} />
            <div className={`flex items-center gap-2 px-3.5 py-3 border-b ${dark ? 'border-white/[0.06]' : 'border-slate-100'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${s.dot} shadow-[0_0_8px_currentColor]`} />
              <span className={`text-[10px] font-bold uppercase tracking-[0.14em] truncate ${dark ? 'text-white/70' : 'text-slate-600'}`}>{s.label}</span>
              <span className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full ${s.chip}`}
                data-testid={`pipeline-count-${s.id}`}>{total}</span>
            </div>
            <div className="p-2 space-y-2 min-h-[200px] max-h-[74vh] overflow-y-auto">
              {cards.map(c => (
                <div key={c.lead_id} draggable
                  onDragStart={e => {
                    e.dataTransfer.setData('leadId', c.lead_id);
                    e.dataTransfer.setData('from', s.id);
                  }}
                  onClick={() => onOpenLead(c.lead_id)}
                  className={`group rounded-xl border p-2.5 cursor-grab active:cursor-grabbing transition-all duration-200 hover:-translate-y-0.5 ${
                    dark
                      ? 'bg-[#15171c] border-white/10 hover:border-teal-400/40 hover:shadow-[0_10px_26px_-14px_rgba(45,212,191,0.4)]'
                      : 'bg-white border-slate-200 shadow-sm hover:border-teal-400/70 hover:shadow-[0_10px_26px_-14px_rgba(20,184,166,0.35)]'}`}
                  data-testid={`pipeline-card-${c.lead_id}`}>
                  <div className="flex items-start gap-2.5 min-w-0">
                    <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${avatarFor(c.name)} flex items-center justify-center shrink-0 text-white text-[11px] font-black`}>
                      {(c.name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-[12px] font-semibold leading-snug truncate ${textPrimary}`}>{c.name}</p>
                      {(c.industry || c.city) && (
                        <p className={`text-[10px] mt-0.5 truncate ${textSecondary}`}>
                          {[c.industry, c.city].filter(Boolean).join(' · ')}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 mt-2">
                    <span title={c.analyzed ? 'Deep analysis done' : 'Not analyzed yet'}
                      className={`w-[22px] h-[22px] rounded-md flex items-center justify-center border ${
                        c.analyzed
                          ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-400'
                          : dark ? 'border-white/[0.07] bg-white/[0.03] text-white/20' : 'border-slate-200 bg-slate-50 text-slate-300'}`}
                      data-testid={`pipeline-ic-analysis-${c.lead_id}`}>
                      <ScanSearch size={11} />
                    </span>
                    <span title={c.has_proposal ? 'Proposal created' : 'No proposal yet'}
                      className={`w-[22px] h-[22px] rounded-md flex items-center justify-center border ${
                        c.has_proposal
                          ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-400'
                          : dark ? 'border-white/[0.07] bg-white/[0.03] text-white/20' : 'border-slate-200 bg-slate-50 text-slate-300'}`}
                      data-testid={`pipeline-ic-proposal-${c.lead_id}`}>
                      <FileText size={11} />
                    </span>
                    {(c.upfront > 0 || c.monthly > 0) && (
                      <span className="ml-auto text-[10px] font-bold text-emerald-400 whitespace-nowrap"
                        data-testid={`pipeline-value-${c.lead_id}`}>
                        {c.upfront > 0 && `$${Number(c.upfront).toLocaleString()}`}
                        {c.upfront > 0 && c.monthly > 0 && ' + '}
                        {c.monthly > 0 && `$${Number(c.monthly).toLocaleString()}/mo`}
                      </span>
                    )}
                  </div>
                </div>
              ))}
              {total > cards.length && (
                <button onClick={() => setExpanded(x => [...x, s.id])}
                  className={`w-full text-[10.5px] font-semibold text-center py-2 rounded-lg transition-colors ${
                    dark ? 'text-teal-300/80 hover:text-teal-300 hover:bg-white/[0.04]' : 'text-teal-600 hover:text-teal-700 hover:bg-slate-100'}`}
                  data-testid={`pipeline-showall-${s.id}`}>
                  Show all {total} (+{total - cards.length} more)
                </button>
              )}
              {total === 0 && (
                <div className={`mx-1 my-3 rounded-xl border border-dashed py-6 text-center text-[10px] ${
                  dark ? 'border-white/10 text-white/25' : 'border-slate-200 text-slate-300'}`}>
                  Drop leads here
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PipelineBoard;
